import {
  getConditionalRules,
  getFieldOptions,
  getFieldTitle,
  getFieldUiType,
  getSectionInfo,
  isActionButtonField,
  isFieldHiddenInEdit,
  isUploadField,
  sortJsonFormFields,
  type JsonFormObjectSchema,
  type JsonFormOptionValue,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";
import {
  isObjectSchema,
  isRecord,
} from "containers/admin/clients/client/components/form-builder/form-builder-utils";

import { EMPTY_DISPLAY_VALUE } from "constants/ui";
import { formatDate } from "utilities/date";

export const READ_ONLY_EMPTY_BLOCK_MESSAGE = "There are no items.";

const READ_ONLY_HIDDEN_SECTION_INFO = "If applicable";

export const getSectionInfoForReadOnly = (
  schema: JsonFormObjectSchema,
): string | undefined => {
  const sectionInfo = getSectionInfo(schema);

  if (!sectionInfo || sectionInfo.trim() === READ_ONLY_HIDDEN_SECTION_INFO) {
    return undefined;
  }

  return sectionInfo;
};

export const shouldShowReadOnlyEmptyState = (schema: JsonFormObjectSchema): boolean =>
  !getSectionInfo(schema);

const formatOptionLabel = (
  field: JsonFormSchema,
  value: JsonFormOptionValue,
): string => {
  const option = getFieldOptions(field).find((entry) => String(entry.value) === String(value));

  return option?.label ?? String(value);
};

const formatOptionLabels = (
  field: JsonFormSchema,
  values: JsonFormOptionValue[],
): string => values.map((value) => formatOptionLabel(field, value)).join(", ");

const formatObjectDisplayValue = (
  field: JsonFormObjectSchema,
  value: Record<string, unknown>,
): string => {
  const parts = sortJsonFormFields(Object.entries(field.properties))
    .map(([name, childField]) => {
      const childValue = value[name];

      if (isEmptyDisplayValue(childValue)) {
        return null;
      }

      return `${getFieldTitle(name, childField)}: ${formatFieldDisplayValue(childField, childValue)}`;
    })
    .filter((part): part is string => part !== null);

  return parts.length > 0 ? parts.join(", ") : EMPTY_DISPLAY_VALUE;
};

export const isEmptyDisplayValue = (value: unknown): boolean => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return true;
  }

  if (isRecord(value)) {
    return Object.keys(value).length === 0 || Object.values(value).every(isEmptyDisplayValue);
  }

  return false;
};

export const hasFieldDisplayContent = (field: JsonFormSchema, value: unknown): boolean => {
  if (isActionButtonField(field)) {
    return true;
  }

  return !isEmptyDisplayValue(value);
};

export type NestedObjectDisplayLine = {
  label: string;
  value: string;
};

export const getNestedObjectDisplayLines = (
  objectSchema: JsonFormObjectSchema,
  value: unknown,
): NestedObjectDisplayLine[] => {
  if (!isRecord(value)) {
    return [];
  }

  return sortJsonFormFields(Object.entries(objectSchema.properties))
    .filter(([propertyName, propertyField]) =>
      hasFieldDisplayContent(propertyField, value[propertyName]),
    )
    .map(([propertyName, propertyField]) => ({
      label: getFieldTitle(propertyName, propertyField),
      value: formatFieldDisplayValue(propertyField, value[propertyName]),
    }));
};

export const formatFieldDisplayValue = (field: JsonFormSchema, value: unknown): string => {
  if (isEmptyDisplayValue(value)) {
    return EMPTY_DISPLAY_VALUE;
  }

  if (isObjectSchema(field) && isRecord(value)) {
    return getNestedObjectDisplayLines(field, value)
      .map(({ label, value: displayValue }) => `${label}: ${displayValue}`)
      .join("\n");
  }

  const uiType = getFieldUiType(field);

  if (uiType === "toggle" || field.type === "boolean") {
    return value ? "Yes" : "No";
  }

  if (uiType === "date") {
    return formatDate(value as string, String(value));
  }

  if (uiType === "checkbox-group" || uiType === "multi-select") {
    const values = Array.isArray(value) ? (value as JsonFormOptionValue[]) : [value as JsonFormOptionValue];

    return formatOptionLabels(field, values);
  }

  if (uiType === "select" || uiType === "select-search" || uiType === "radio-group" || field.enum) {
    return formatOptionLabel(field, value as JsonFormOptionValue);
  }

  if (isObjectSchema(field) && isRecord(value)) {
    return formatObjectDisplayValue(field, value);
  }

  return String(value);
};

export const isDynamicSectionField = (field: JsonFormSchema): boolean =>
  field.type === "array" && getFieldUiType(field) === "dynamic-section";

export const isKeyValueTableField = (field: JsonFormSchema): boolean =>
  !isObjectSchema(field) &&
  !isDynamicSectionField(field) &&
  !isActionButtonField(field) &&
  !isUploadField(field);

export const isFlatDynamicSectionItemSchema = (itemSchema: JsonFormObjectSchema): boolean =>
  Object.values(itemSchema.properties).every((property) => isKeyValueTableField(property));

export type FlatDynamicSectionTableColumn = {
  id: string;
  label: string;
  field: JsonFormSchema;
};

export type FlatDynamicSectionTableRow = {
  id: string;
  cells: Record<string, string>;
  rawCells: Record<string, unknown>;
};

export const buildFlatDynamicSectionTableData = (
  itemSchema: JsonFormObjectSchema,
  rows: Record<string, unknown>[],
): { columns: FlatDynamicSectionTableColumn[]; rows: FlatDynamicSectionTableRow[] } => {
  const columns = sortJsonFormFields(Object.entries(itemSchema.properties))
    .filter(
      ([name, field]) =>
        isKeyValueTableField(field) &&
        !isFieldHiddenInEdit(field) &&
        hasFieldDisplayLabel(name, field) &&
        rows.some((row) => hasFieldDisplayContent(field, row[name])),
    )
    .map(([name, field]) => ({
      id: name,
      label: getFieldTitle(name, field),
      field,
    }));

  const tableRows = rows.flatMap((row, index) => {
    const hasContent = columns.some(({ id }) => {
      const field = itemSchema.properties[id];

      return field && hasFieldDisplayContent(field, row[id]);
    });

    if (!hasContent) {
      return [];
    }

    return [
      {
        id: `flat-dynamic-section-row-${index}`,
        rawCells: Object.fromEntries(columns.map(({ id }) => [id, row[id]])),
        cells: Object.fromEntries(
          columns.map(({ id }) => {
            const field = itemSchema.properties[id];
            const value = row[id];

            return [
              id,
              field && hasFieldDisplayContent(field, value)
                ? formatFieldDisplayValueForReadOnly(field, value)
                : EMPTY_DISPLAY_VALUE,
            ];
          }),
        ),
      },
    ];
  });

  return { columns, rows: tableRows };
};

export const hasFieldDisplayLabel = (fieldName: string, field: JsonFormSchema): boolean =>
  getFieldTitle(fieldName, field).trim().length > 0;

export const isConditionalTriggerField = (
  fieldName: string,
  parentSchema: JsonFormObjectSchema,
): boolean =>
  getConditionalRules(parentSchema).some((rule) => rule.triggerFieldName === fieldName);

export const formatFieldDisplayValueForReadOnly = (
  field: JsonFormSchema,
  value: unknown,
): string => {
  const uiType = getFieldUiType(field);

  if (uiType === "checkbox-group" && Array.isArray(value) && value.length > 0) {
    return (value as JsonFormOptionValue[])
      .map((entry) => formatOptionLabel(field, entry))
      .join("\n");
  }

  return formatFieldDisplayValue(field, value);
};

export const buildDynamicSectionItemCopyText = (
  row: Record<string, unknown>,
  itemSchema: JsonFormObjectSchema,
): string =>
  sortJsonFormFields(Object.entries(itemSchema.properties))
    .flatMap(([name, field]) => {
      const value = row[name];

      if (isEmptyDisplayValue(value)) {
        return [];
      }

      if (isObjectSchema(field) && isRecord(value)) {
        return getNestedObjectDisplayLines(field, value).map(
          ({ label, value: displayValue }) => `${label}: ${displayValue}`,
        );
      }

      return [`${getFieldTitle(name, field)}: ${formatFieldDisplayValueForReadOnly(field, value)}`];
    })
    .join("\n");
